import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTestOrder } from '@/hooks/use-test-order';
import { useTestCatalog } from '@/hooks/use-test-catalog';
import TestSelection from '@/components/test-order/TestSelection';
import OrderSummary from '@/components/test-order/OrderSummary';
import Loading from '@/components/ui/Loading';
import ErrorMessage from '@/components/ui/ErrorMessage';
import { Button } from '@/components/ui/button';
import { ArrowLeft, CalendarClock } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const OrderTest: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const testId = params.get('testId');

  const {
    orderedTests,
    addTest,
    removeTest,
    updateQuantity,
    clearOrder,
    submitOrder,
    notes,
    setNotes,
    isSubmitting,
    submitError
  } = useTestOrder();

  const { tests, isLoading: isLoadingCatalog, error: catalogError } = useTestCatalog();

  // Automatically add the test from URL param if available
  useEffect(() => {
    if (testId && tests.length > 0) {
      const testToAdd = tests.find(test => test.id === testId);
      if (testToAdd && !orderedTests.some(t => t.testId === testId)) {
        addTest(testToAdd);
      }
    }
  }, [testId, tests, addTest, orderedTests]);

  // Navigate back to catalog
  const handleBackToCatalog = () => {
    navigate('/test-catalog');
  };

  // Submit and navigate to scheduling
  const handleSubmitAndSchedule = () => {
    submitOrder();
  };

  if (isLoadingCatalog) {
    return <Loading text="Loading test catalog..." />;
  }

  if (catalogError) {
    return <ErrorMessage message="Failed to load the test catalog. Please try again later." />;
  }

  return (
    <div className="container p-4 max-w-6xl mx-auto">
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold tracking-tight">Order Tests</h1>
          <Button variant="outline" size="sm" onClick={handleBackToCatalog}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Catalog
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Test Selection */}
          <div className="lg:col-span-2 space-y-4">
            <TestSelection 
              availableTests={tests} 
              onAddTest={addTest}
              selectedTestIds={new Set(orderedTests.map(t => t.testId))}
            />
          </div>

          {/* Right Column: Order Summary */}
          <div className="space-y-4">
            <OrderSummary 
              tests={orderedTests}
              onRemove={removeTest}
              onUpdateQuantity={updateQuantity}
              onClear={clearOrder}
              notes={notes}
              onNotesChange={setNotes}
            />

            {submitError && (
              <Alert variant="destructive">
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>
                  {submitError instanceof Error 
                    ? submitError.message 
                    : 'Failed to submit order. Please try again.'}
                </AlertDescription>
              </Alert>
            )}

            <Button 
              disabled={orderedTests.length === 0 || isSubmitting}
              onClick={handleSubmitAndSchedule}
              className="w-full"
            >
              <CalendarClock className="mr-2 h-4 w-4" />
              {isSubmitting ? 'Submitting...' : 'Continue to Scheduling'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTest; 